import { useState, useEffect, useContext, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead } from '../sections/@dashboard/user';
import { PendingSignaturesToolbar } from '../sections/@dashboard/pendingSignatures';

import { EntityContext } from '../entity';
import { AccountContext } from '../account';
import { isBatchEndorsementPayload } from '../utils/batchEndorsement';
import { signatureTabOptions } from '../utils/signatureMethods';

// ----------------------------------------------------------------------

const MAX_SELECTION_LIMIT = 500;

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', alignRight: false },
  { id: 'company', label: 'Data de criação', alignRight: false },
  { id: 'role', label: 'Ultima alteração', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

const BATCH_TABLE_HEAD = [
  { id: 'batch_envelope_name', label: 'Nome do Lote', alignRight: false },
  { id: 'batch_envelope_type', label: 'Tipo', alignRight: false },
  { id: 'created_at', label: 'Data de criação', alignRight: false },
  { id: 'updated_at', label: 'Ultima alteração', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, fieldName = 'envelope_name') {
  if (!array || array.length === 0) {
    return [];
  }
  
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  
  if (query) {
    return filter(array, (_item) => {
      const fieldValue = _item[fieldName];
      return fieldValue && fieldValue.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  
  return stabilizedThis.map((el) => el[0]);
}

// Função para formatar datas
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return 'N/A';
  }
}

// Função para validar se pode adicionar mais itens
function canAddMoreItems(currentCount) {
  return currentCount < MAX_SELECTION_LIMIT;
}

// Função para mostrar alerta de limite atingido
function showLimitAlert() {
  alert(`Limite máximo de ${MAX_SELECTION_LIMIT} documentos atingido. Remova alguns itens para continuar selecionando.`);
}

export default function PendingSignaturesPage() {
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [pendingBatchSignatures, setPendingBatchSignatures] = useState([]);

  // Pagination state for individual signatures (baseado na página de envelopes)
  const [loadedPages, setLoadedPages] = useState(new Set([1]));
  const [totalResults, setTotalResults] = useState(0); // Total real da API (como na página de envelopes)
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Pagination state for batch signatures
  const [batchLoadedPages, setBatchLoadedPages] = useState(new Set([1]));
  const [batchTotalResults, setBatchTotalResults] = useState(0);
  const [batchIsLoadingMore, setBatchIsLoadingMore] = useState(false);

  const [token, setToken] = useState(null)
  const [open, setOpen] = useState(null);

  // Tab state for individual signatures
  const [currentTab, setCurrentTab] = useState('email');
  // WhatsApp não entra no lote do usuário logado (o backend recusa em /signature/batch); ICP entra (US-019)
  const batchAllowed = currentTab !== 'whatsapp';
  const [selectedByTab, setSelectedByTab] = useState({
    email: [],
    selfie: [],
    icp: [],
  });

  // Individual signatures state
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Batch signatures state
  const [batchPage, setBatchPage] = useState(0);
  const [batchOrder, setBatchOrder] = useState('asc');
  const [batchSelected, setBatchSelected] = useState([]);
  const [batchOrderBy, setBatchOrderBy] = useState('batch_envelope_name');
  const [batchFilterName, setBatchFilterName] = useState('');
  const [batchRowsPerPage, setBatchRowsPerPage] = useState(5);

  const { getPendingSignatures, getPendingBatchSignatures, createBatchSignature, requestBatchSignatureToken, signatureMethods } = useContext(EntityContext)
  const { getSession } = useContext(AccountContext)

  useEffect(() => {
    getSession()
        .then((data) => {
            setToken(data.idToken.jwtToken)
        })
    }, [])

  useEffect(() => {
    GetUserInfo()
    }, [token])

  const GetUserInfo = useCallback(async () => {
    if (token) {
      await handleSetUserInfo(token)
    }
  }, [token])

  const handleSetUserInfo = async (token) => {
    try {
      // Carregar assinaturas individuais da aba inicial (email)
      const individualData = await getPendingSignatures(token, 1, currentTab);
      if (individualData && !individualData.error) {
        setPendingSignatures(individualData.pending_signatures || []);
        setTotalResults(individualData.total_results || 0); // Total específico da aba
      }

      // Carregar assinaturas em lote
      const batchData = await getPendingBatchSignatures(token, 1);
      if (batchData && !batchData.error) {
        setPendingBatchSignatures(batchData.pending_batch_signatures || []);
        setBatchTotalResults(batchData.total_results || 0);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }

  // Função para carregar mais dados de assinaturas individuais (baseada na página de envelopes)
  const loadMoreIndividualDataIfNeeded = useCallback(async (currentPage) => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    // Calcular qual página da API precisamos (500 por página)
    const apiPageNeeded = Math.ceil((endIndex + 1) / 500);
    
    // Verificar se já carregamos essa página e se não excedemos o total (como na página de envelopes)
    if (!loadedPages.has(apiPageNeeded) && endIndex <= totalResults && totalResults > 0) {
      setIsLoadingMore(true);
      
      try {
        const data = await getPendingSignatures(token, apiPageNeeded, currentTab);
        if (data && !data.error && data.pending_signatures) {
          // Adicionar novos dados ao array existente
          setPendingSignatures(prevList => [...prevList, ...data.pending_signatures]);
          setLoadedPages(prev => new Set([...prev, apiPageNeeded]));
        }
      } catch (error) {
        console.error('Erro ao carregar mais dados de assinaturas individuais:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [loadedPages, rowsPerPage, totalResults, token, getPendingSignatures, currentTab]);

  // Função para carregar mais dados de assinaturas em lote
  const loadMoreBatchDataIfNeeded = useCallback(async (currentPage) => {
    const startIndex = currentPage * batchRowsPerPage;
    const endIndex = startIndex + batchRowsPerPage;
    
    // Calcular qual página da API precisamos (500 por página)
    const apiPageNeeded = Math.ceil((endIndex + 1) / 500);
    
    // Verificar se já carregamos essa página e se não excedemos o total
    if (!batchLoadedPages.has(apiPageNeeded) && apiPageNeeded <= Math.ceil(batchTotalResults / 500) && batchTotalResults > 0) {
      setBatchIsLoadingMore(true);
      
      try {
        const data = await getPendingBatchSignatures(token, apiPageNeeded);
        if (data && !data.error && data.pending_batch_signatures) {
          // Adicionar novos dados ao array existente
          setPendingBatchSignatures(prevList => [...prevList, ...data.pending_batch_signatures]);
          setBatchLoadedPages(prev => new Set([...prev, apiPageNeeded]));
        }
      } catch (error) {
        console.error('Erro ao carregar mais dados de assinaturas em lote:', error);
      } finally {
        setBatchIsLoadingMore(false);
      }
    }
  }, [batchLoadedPages, batchRowsPerPage, batchTotalResults, token, getPendingBatchSignatures]);

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  // Tab handlers
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0); // Reset page when changing tabs
    setFilterName(''); // Reset filter when changing tabs
    setLoadedPages(new Set([1])); // Reset loaded pages
    setPendingSignatures([]); // Clear current data
    
    // Recarregar dados para a nova aba
    if (token) {
      loadTabData(token, newValue);
    }
  };

  // Função para carregar dados específicos de uma aba
  const loadTabData = async (token, tabType) => {
    try {
      const data = await getPendingSignatures(token, 1, tabType);
      if (data && !data.error) {
        setPendingSignatures(data.pending_signatures || []);
        setTotalResults(data.total_results || 0); // Total específico da aba
      } else {
        // Se houver erro, limpar os dados e definir total como 0
        setPendingSignatures([]);
        setTotalResults(0);
        console.error('Error loading tab data:', data?.error);
      }
    } catch (error) {
      console.error('Error loading tab data:', error);
      // Em caso de erro, limpar os dados
      setPendingSignatures([]);
      setTotalResults(0);
    }
  };

  // Get current tab signatures (API já retorna apenas o tipo específico)
  const getCurrentTabSignatures = () => {
    return pendingSignatures;
  };

  // Get current tab selected items
  const getCurrentTabSelected = () => {
    return selectedByTab[currentTab] || [];
  };

  // Individual signatures handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    // Não permitir seleção múltipla onde não há lote
    if (!batchAllowed) return;
    
    const currentTabSignatures = getCurrentTabSignatures();
    if (event.target.checked) {
      // Limitar a seleção ao máximo permitido
      const maxSelectable = Math.min(currentTabSignatures.length, MAX_SELECTION_LIMIT);
      const newSelecteds = currentTabSignatures.slice(0, maxSelectable).map((n) => n.envelope_signer_key || n.signer_key || n.id);
      
      setSelectedByTab(prev => ({
        ...prev,
        [currentTab]: newSelecteds
      }));

      // Mostrar alerta se não foi possível selecionar todos
      if (currentTabSignatures.length > MAX_SELECTION_LIMIT) {
        alert(`Selecionados ${maxSelectable} documentos (limite máximo). ${currentTabSignatures.length - maxSelectable} documentos restantes não puderam ser selecionados.`);
      }
      return;
    }
    setSelectedByTab(prev => ({
      ...prev,
      [currentTab]: []
    }));
  };

  const handleClick = (event, name) => {
    // Não permitir seleção múltipla onde não há lote
    if (!batchAllowed) return;
    
    const currentSelected = getCurrentTabSelected();
    const selectedIndex = currentSelected.indexOf(name);
    let newSelected = [];
    
    if (selectedIndex === -1) {
      // Verificar se pode adicionar mais itens
      if (!canAddMoreItems(currentSelected.length)) {
        showLimitAlert();
        return;
      }
      newSelected = newSelected.concat(currentSelected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(currentSelected.slice(1));
    } else if (selectedIndex === currentSelected.length - 1) {
      newSelected = newSelected.concat(currentSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(currentSelected.slice(0, selectedIndex), currentSelected.slice(selectedIndex + 1));
    }
    
    setSelectedByTab(prev => ({
      ...prev,
      [currentTab]: newSelected
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Verificar se precisamos carregar mais dados
    loadMoreIndividualDataIfNeeded(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // Verificar se precisamos carregar mais dados quando rowsPerPage mudar
  useEffect(() => {
    if (page > 0) {
      loadMoreIndividualDataIfNeeded(page);
    }
  }, [rowsPerPage, page]); // Removida a dependência loadMoreIndividualDataIfNeeded

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  // Batch signatures handlers
  const handleBatchRequestSort = (event, property) => {
    const isAsc = batchOrderBy === property && batchOrder === 'asc';
    setBatchOrder(isAsc ? 'desc' : 'asc');
    setBatchOrderBy(property);
  };

  const handleBatchSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = pendingBatchSignatures.map((n) => n.batch_envelope_signer_key || n.signer_key || n.id);
      setBatchSelected(newSelecteds);
      return;
    }
    setBatchSelected([]);
  };

  const handleBatchClick = (event, name) => {
    const selectedIndex = batchSelected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(batchSelected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(batchSelected.slice(1));
    } else if (selectedIndex === batchSelected.length - 1) {
      newSelected = newSelected.concat(batchSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(batchSelected.slice(0, selectedIndex), batchSelected.slice(selectedIndex + 1));
    }
    setBatchSelected(newSelected);
  };

  const handleBatchChangePage = (event, newPage) => {
    setBatchPage(newPage);
    // Verificar se precisamos carregar mais dados
    loadMoreBatchDataIfNeeded(newPage);
  };

  const handleBatchChangeRowsPerPage = (event) => {
    setBatchPage(0);
    setBatchRowsPerPage(parseInt(event.target.value, 10));
  };

  // Verificar se precisamos carregar mais dados de lote quando rowsPerPage mudar
  useEffect(() => {
    if (batchPage > 0) {
      loadMoreBatchDataIfNeeded(batchPage);
    }
  }, [batchRowsPerPage, batchPage]); // Removida a dependência loadMoreBatchDataIfNeeded

  const handleBatchFilterByName = (event) => {
    setBatchPage(0);
    setBatchFilterName(event.target.value);
  };

  const handleBatchSignature = () => {
    const currentSelected = getCurrentTabSelected();
    if (currentSelected.length === 0) return;
    
    try {
      createBatchSignature(token, currentSelected)
      .then((res) => {
        const batchSignatureKey = res.batch_signature_key
        // ICP: sem token OTP; a tela pede o certificado e os tokens (RN-01)
        if (currentTab === 'icp') {
          window.open(`/signature/${batchSignatureKey}/batch/icp-user`, '_blank', 'noopener,noreferrer');
          return;
        }
        requestBatchSignatureToken(token, batchSignatureKey)
        .then(() => {
          window.open(`/signature/${batchSignatureKey}/batch/token`, '_blank', 'noopener,noreferrer');
        })
      })
    } catch (error) {
      console.log(error)
    }
  }

  const handleSign = (signerKey) => {
    window.open(`/sign/${signerKey}`, '_blank', 'noopener,noreferrer');
  }

  const handleBatchSign = (row) => {
    const key = row.batch_envelope_signer_key || row.signer_key || row.id;
    const base = isBatchEndorsementPayload(row) ? '/endorsement' : '/signature';
    window.open(`${base}/${key}/batch`, '_blank', 'noopener,noreferrer');
  };

  // Individual signatures calculations
  const currentTabSignatures = getCurrentTabSignatures();
  const currentTabSelected = getCurrentTabSelected();
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - currentTabSignatures.length) : 0;
  const filteredUsers = applySortFilter(currentTabSignatures, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredUsers.length && !!filterName;

  // Batch signatures calculations
  const batchEmptyRows = batchPage > 0 ? Math.max(0, (1 + batchPage) * batchRowsPerPage - pendingBatchSignatures.length) : 0;
  const filteredBatchSignatures = applySortFilter(pendingBatchSignatures, getComparator(batchOrder, batchOrderBy), batchFilterName, 'batch_envelope_name');
  const isBatchNotFound = !filteredBatchSignatures.length && !!batchFilterName;

  return (
    <>
      <Helmet>
        <title> Envelopes | Minimal UI </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Assinaturas Pendentes
          </Typography>
        </Stack>

        {/* Individual Signatures Table */}
        <Card sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Typography variant="h6">
              Assinaturas Individuais
            </Typography>
          </Stack>

          {/* Tabs for signature types */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="signature type tabs">
              {signatureTabOptions(signatureMethods).map((option) => (
                <Tab key={option.value} label={option.label} value={option.value} />
              ))}
            </Tabs>
          </Box>

          <PendingSignaturesToolbar 
            numSelected={currentTabSelected.length} 
            filterName={filterName} 
            onFilterName={handleFilterByName} 
            onBatchSignature={handleBatchSignature}
            currentTab={currentTab}
            showBatchButton={batchAllowed}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={totalResults}
                  numSelected={currentTabSelected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  showCheckbox={batchAllowed}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const selectedUser = currentTabSelected.indexOf(row.envelope_signer_key || row.signer_key || row.id) !== -1;

                    return (
                      <TableRow hover key={row.id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        {batchAllowed && (
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, row.envelope_signer_key || row.signer_key || row.id)} />
                          </TableCell>
                        )}

                        <TableCell component="th" scope="row" padding={batchAllowed ? 'none' : 'normal'}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                            {row.envelope_name || row.document_name || 'Sem nome'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{formatDate(row.created_at)}</TableCell>

                        <TableCell align="left">{formatDate(row.updated_at)}</TableCell>

                        <TableCell align="left">
                          <Label>{row.status}</Label>
                        </TableCell>

                        <TableCell align="right">
                          <Button size="large" color="inherit" onClick={_ => handleSign(row.envelope_signer_key || row.signer_key || row.id)}>
                            <Iconify icon={'eva:edit-fill'} />Assinar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={batchAllowed ? 6 : 5} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={batchAllowed ? 6 : 5} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalResults}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          
          {isLoadingMore && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Carregando mais dados...
              </Typography>
            </Box>
          )}
        </Card>

        {/* Batch Signatures Table */}
        <Card sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Typography variant="h6">
              Assinaturas em Lote
            </Typography>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, pr: 2 }}>
              <Table>
                <UserListHead
                  order={batchOrder}
                  orderBy={batchOrderBy}
                  headLabel={BATCH_TABLE_HEAD}
                  rowCount={batchTotalResults}
                  numSelected={0}
                  onRequestSort={handleBatchRequestSort}
                  onSelectAllClick={() => {}}
                  showCheckbox={false}
                />
                <TableBody>
                  {pendingBatchSignatures.length === 0 ? (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Nenhuma assinatura em lote pendente
                          </Typography>

                          <Typography variant="body2">
                            Não há assinaturas em lote pendentes no momento.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBatchSignatures.slice(batchPage * batchRowsPerPage, batchPage * batchRowsPerPage + batchRowsPerPage).map((row) => {
                      const selectedBatch = batchSelected.indexOf(row.batch_envelope_signer_key || row.signer_key || row.id) !== -1;

                      return (
                        <TableRow hover key={row.id} tabIndex={-1}>

                          <TableCell component="th" scope="row" padding="normal">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                              {row.batch_envelope_name || 'Sem nome'}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">
                            <Label variant="soft" color="info">
                              {row.signature_type || 'N/A'}
                            </Label>
                          </TableCell>

                          <TableCell align="left">{formatDate(row.created_at)}</TableCell>

                          <TableCell align="left">{formatDate(row.updated_at)}</TableCell>

                          <TableCell align="left">
                            <Label>{row.status || 'N/A'}</Label>
                          </TableCell>

                          <TableCell align="right">
                            <Button size="large" color="inherit" onClick={() => handleBatchSign(row)}>
                              <Iconify icon={'eva:edit-fill'} />Assinar Lote
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                  {batchEmptyRows > 0 && (
                    <TableRow style={{ height: 53 * batchEmptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>

                {isBatchNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{batchFilterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={batchTotalResults}
            rowsPerPage={batchRowsPerPage}
            page={batchPage}
            onPageChange={handleBatchChangePage}
            onRowsPerPageChange={handleBatchChangeRowsPerPage}
          />
          
          {batchIsLoadingMore && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Carregando mais dados...
              </Typography>
            </Box>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={e => console.log(e.target.value)}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

      </Popover>
    </>
  );
}
